import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { ThinkfabLogo } from '../Logo';
import mediaQueries from '../../styles/mediaQueries';

interface HeaderContainerProps {
  backgroundColor?: string;
}

const HeaderContainer = styled.header<HeaderContainerProps>`
  background-color: ${props => 
    props.backgroundColor === "transparent" 
      ? "transparent" 
      : props.backgroundColor || props.theme.colors.primary};
  padding: 20px 0;
  position: sticky;
  top: 0;
  z-index: 100;
  font-family: var(--font-primary);
  width: 100%;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
`;

const NavLogo = styled(ThinkfabLogo)`
  font-size: 24px;
  z-index: 10;
`;

const Navigation = styled.nav<{ isOpen: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 30px;
  align-items: center;
  font-weight: var(--font-weight-medium);
  
  ${mediaQueries.medium} {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transform: none;
    flex-direction: column;
    justify-content: center;
    background-color: rgba(1, 29, 58, 0.95);
    z-index: 9;
    gap: 40px;
    opacity: ${props => (props.isOpen ? 1 : 0)};
    pointer-events: ${props => (props.isOpen ? 'all' : 'none')};
    transition: opacity 0.3s ease;
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 10px;
  cursor: pointer;
  z-index: 10;
  
  ${mediaQueries.medium} {
    display: block;
    position: absolute;
    right: 20px;
  }
  
  span {
    display: block;
    width: 24px;
    height: 2px;
    margin-bottom: 5px;
    position: relative;
    background: currentColor;
    border-radius: 3px;
    z-index: 1;
    transform-origin: 4px 0px;
    transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1.0),
                background 0.5s cubic-bezier(0.77, 0.2, 0.05, 1.0),
                opacity 0.55s ease;
    
    &:first-child {
      transform-origin: 0% 0%;
    }
    
    &:nth-last-child(2) {
      transform-origin: 0% 100%;
    }
  }
`;

const MenuToggleClose = styled(MenuToggle)<{ isOpen: boolean }>`
  position: fixed;
  right: 20px;
  top: 20px;
  z-index: 11;
  display: none;
  color: white;
  
  ${mediaQueries.medium} {
    display: ${props => (props.isOpen ? 'block' : 'none')};
  }
  
  span {
    &:first-child {
      transform: rotate(45deg) translate(2px, -1px);
    }
    
    &:nth-child(2) {
      opacity: 0;
    }
    
    &:nth-last-child(1) {
      transform: rotate(-45deg) translate(0, -1px);
    }
  }
`;

interface NavLinkProps {
  isActive: boolean;
}

interface NavItemProps extends NavLinkProps {
  backgroundColor?: string;
  textColor?: string;
}

const NavItem = styled(Link)<NavItemProps>`
  color: ${props => 
    props.textColor ? props.textColor : 
    props.backgroundColor === "white" ? props.theme.colors.primary : "white"};
  text-decoration: none;
  font-size: 14px;
  position: relative;
  transition: all 0.3s ease;
  opacity: ${props => (props.isActive ? 1 : 0.8)};
  
  &:after {
    content: '';
    position: absolute;
    width: ${props => (props.isActive ? '100%' : '0')};
    height: 2px;
    bottom: -5px;
    left: 0;
    background-color: ${props => 
      props.textColor ? props.textColor : 
      props.backgroundColor === "white" ? props.theme.colors.primary : "white"};
    transition: width 0.3s ease;
  }
  
  &:hover {
    opacity: 1;
    
    &:after {
      width: 100%;
    }
  }
  
  ${mediaQueries.medium} {
    font-size: 20px;
    margin: 10px 0;
    color: white;
    
    &:after {
      background-color: white;
    }
  }
`;

interface SocialIconProps {
  backgroundColor?: string;
  textColor?: string;
}

const SocialIcon = styled.a<SocialIconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => 
    props.textColor ? props.textColor : 
    props.backgroundColor === "white" ? props.theme.colors.primary : "white"};
  opacity: 0.8;
  transition: opacity 0.3s;
  position: absolute;
  right: 20px;
  
  &:hover {
    opacity: 1;
  }

  svg {
    width: 20px;
    height: 20px;
  }
  
  ${mediaQueries.medium} {
    display: none;
  }
`;

interface HeaderProps {
  className?: string;
  backgroundColor?: string;
  textColor?: string;
}

const Header: React.FC<HeaderProps> = ({ className, backgroundColor, textColor }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Define different colors based on the page and provided text color
  const getLogoColors = () => {
    // If textColor is explicitly provided, use it for the logo
    if (textColor) {
      return {
        thinkfabColor: textColor,
        separatorColor: textColor
      };
    }
    
    // Use white for logo on blue or transparent backgrounds without specific textColor
    if (backgroundColor === undefined || backgroundColor === '#011D3A' || backgroundColor === "transparent") {
      return {
        thinkfabColor: 'white',
        separatorColor: 'white'
      };
    }
    
    // For all other backgrounds, use blue for the logo
    return {
      thinkfabColor: '#011D3A',
      separatorColor: '#011D3A'
    };
  };

  const logoColors = getLogoColors();
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  
  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <HeaderContainer className={className} backgroundColor={backgroundColor}>
      <HeaderContent>
        <Link to="/">
          <NavLogo 
            thinkfabColor={logoColors.thinkfabColor}
            separatorColor={logoColors.separatorColor}
          />
        </Link>
        
        <Navigation isOpen={menuOpen}>
          <NavItem 
            to="/agence" 
            isActive={isActive('/agence')} 
            backgroundColor={backgroundColor} 
            textColor={textColor}
            onClick={closeMenu}
          >
            L'agence
          </NavItem>
          <NavItem 
            to="/metier" 
            isActive={isActive('/metier')} 
            backgroundColor={backgroundColor} 
            textColor={textColor}
            onClick={closeMenu}
          >
            Notre métier
          </NavItem>
          <NavItem 
            to="/projets" 
            isActive={isActive('/projets')} 
            backgroundColor={backgroundColor} 
            textColor={textColor}
            onClick={closeMenu}
          >
            Les projets
          </NavItem>
          <NavItem 
            to="/innopath" 
            isActive={isActive('/innopath')} 
            backgroundColor={backgroundColor} 
            textColor={textColor}
            onClick={closeMenu}
          >
            InnoPath
          </NavItem>
          <NavItem 
            to="/recrutement" 
            isActive={isActive('/recrutement') || isActive('/talents')} 
            backgroundColor={backgroundColor} 
            textColor={textColor}
            onClick={closeMenu}
          >
            Recrutement
          </NavItem>
        </Navigation>
        
        {/* Menu toggle for mobile */}
        <MenuToggle 
          onClick={toggleMenu}
          aria-label="Menu"
          style={{ color: textColor || (backgroundColor === "white" ? "#011D3A" : "white") }}
        >
          <span></span>
          <span></span>
          <span></span>
        </MenuToggle>
        
        <MenuToggleClose 
          isOpen={menuOpen} 
          onClick={toggleMenu}
          aria-label="Close Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </MenuToggleClose>
        
        <SocialIcon 
          href="https://linkedin.com/company/thinkfab" 
          target="_blank" 
          rel="noopener noreferrer" 
          aria-label="LinkedIn"
          backgroundColor={backgroundColor}
          textColor={textColor}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
          </svg>
        </SocialIcon>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;